import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { Profile } from '../../lib/supabase';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{
    characters: Array<{ id: number; name: string; anime_name: string; image_url: string }>;
    series: Array<{ name: string; character_count: number; cover_image: string }>;
  }>({ characters: [], series: [] });
  const [searching, setSearching] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);

  useEffect(() => {
    if (user && !authLoading) {
      fetchProfile();
    } else if (!user) {
      // Clear profile data when user is logged out
      setUsername('');
      setIsAdmin(false);
      setProfile(null);
      setProfileLoading(false);
    }
  }, [user, authLoading]);

  const fetchProfile = async () => {
    if (!user) return;
    
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        // If profile doesn't exist, create a default username
        setUsername(`user_${user.id.slice(0, 8)}`);
        setIsAdmin(false);
        setProfile(null);
      } else if (data) {
        setUsername(data.username || `user_${user.id.slice(0, 8)}`);
        setIsAdmin(data.role === 'admin');
        setProfile(data);
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      setUsername(`user_${user.id.slice(0, 8)}`);
      setIsAdmin(false);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const getAvatarStyle = () => {
    if (!profile?.avatar_zoom) {
      return {
        objectFit: 'cover' as const,
        objectPosition: 'center',
      };
    }

    const zoom = profile.avatar_zoom;
    const containerSize = 40; // navbar avatar size
    
    const cropWidth = zoom.width;
    const cropHeight = zoom.height;
    
    const scale = containerSize / cropWidth;
    
    const offsetX = -zoom.x * scale;
    const offsetY = -zoom.y * scale;

    return {
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      transformOrigin: 'top left',
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
    };
  };

  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults({ characters: [], series: [] });
        return;
      }

      setSearching(true);
      
      try {
        // Search for characters by character_name or anime_name
        const { data: characterData, error } = await supabase
          .from('anime_characters')
          .select('id, character_name, anime_name, image_url')
          .or(`character_name.ilike.%${searchQuery}%,anime_name.ilike.%${searchQuery}%`)
          .limit(20);

        if (error) {
          console.error('Search error:', error);
          setSearchResults({ characters: [], series: [] });
          setSearching(false);
          return;
        }

        if (characterData) {
          // Filter characters that match by character_name
          const characters = characterData
            .filter(item => 
              item.character_name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .slice(0, 5)
            .map(item => ({
              id: item.id,
              name: item.character_name,
              anime_name: item.anime_name,
              image_url: item.image_url
            }));

          // Get unique series that match the search
          const seriesMap = new Map<string, { name: string; character_count: number; cover_image: string }>();
          
          characterData.forEach(item => {
            if (item.anime_name.toLowerCase().includes(searchQuery.toLowerCase())) {
              const existing = seriesMap.get(item.anime_name);
              if (existing) {
                existing.character_count++;
              } else {
                seriesMap.set(item.anime_name, {
                  name: item.anime_name,
                  character_count: 1,
                  cover_image: item.image_url
                });
              }
            }
          });

          setSearchResults({
            characters,
            series: Array.from(seriesMap.values()).slice(0, 5)
          });
        } else {
          setSearchResults({ characters: [], series: [] });
        }
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults({ characters: [], series: [] });
      }

      setSearching(false);
    };

    const debounce = setTimeout(performSearch, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
  };

  const handleSearchClose = () => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults({ characters: [], series: [] });
  };

  const handleResultClick = () => {
    handleSearchClose();
  };

  // Get display username with better fallback logic
  const getDisplayUsername = () => {
    if (profileLoading || authLoading) {
      return '...'; // Show loading indicator
    }
    if (username) {
      return username;
    }
    if (user) {
      return `user_${user.id.slice(0, 8)}`; // Fallback to user ID
    }
    return 'User'; // Final fallback
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/cd80e8551adaa83aa4e45d1e4f56c590/f41128b12692ca52858c5e3a8248b8bb.png" 
              alt="Anime HOT or NOT Logo" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-lg font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent whitespace-nowrap">
              Anime HOT or NOT
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/rate"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Rate
            </Link>
            <Link
              to="/top10"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Top 10
            </Link>
            <Link
              to="/top5users"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Top 5 Raters
            </Link>
            <Link
              to="/series"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Series
            </Link>
            <Link
              to="/about"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              About
            </Link>
            <Link
              to="/faq"
              className="text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              FAQ
            </Link>

            {/* Search Button */}
            <button
              onClick={() => setShowSearch(true)}
              className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              aria-label="Search"
            >
              <i className="ri-search-line text-xl"></i>
            </button>

            {user ? (
              <>
                {/* Notification Bell */}
                <NotificationBell />

                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    {profile?.avatar_url ? (
                      <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-pink-500 bg-gray-100">
                        <img
                          src={profile.avatar_url}
                          alt="Profile"
                          className="w-full h-full"
                          style={getAvatarStyle()}
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center">
                        <i className="ri-user-line text-xl text-white"></i>
                      </div>
                    )}
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl py-2 border border-gray-100">
                      <Link
                        to={`/profile/${user.id}`}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-user-line mr-2"></i>
                        Profile
                      </Link>
                      <Link
                        to="/favorites"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-heart-line mr-2"></i>
                        Favorites
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-admin-line mr-2"></i>
                          Admin
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          handleSignOut();
                          setShowUserMenu(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <button
                onClick={() => {
                  setShowAuthModal(true);
                  setShowMobileMenu(false);
                }}
                className="px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Mobile Right Side Icons */}
          <div className="md:hidden flex items-center gap-2">
            {/* Notification Bell - Show when logged in */}
            {user && <NotificationBell />}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`ri-${showMobileMenu ? 'close' : 'menu'}-line text-2xl text-gray-700`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden py-2 border-t border-gray-100">
            {/* Search Button in Mobile Menu */}
            <button
              onClick={() => {
                setShowSearch(true);
                setShowMobileMenu(false);
              }}
              className="flex items-center gap-2 w-full py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
            >
              <i className="ri-search-line text-xl"></i>
              Search
            </button>
            <Link
              to="/rate"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              Rate
            </Link>
            <Link
              to="/top10"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              Top 10
            </Link>
            <Link
              to="/top5users"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              Top 5 Raters
            </Link>
            <Link
              to="/series"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              Series
            </Link>
            <Link
              to="/about"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              About
            </Link>
            <Link
              to="/faq"
              className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
              onClick={() => setShowMobileMenu(false)}
            >
              FAQ
            </Link>

            {user ? (
              <>
                <Link
                  to={`/profile/${user.id}`}
                  className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <i className="ri-user-line mr-2"></i>
                  Profile
                </Link>
                <Link
                  to="/favorites"
                  className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <i className="ri-heart-line mr-2"></i>
                  Favorites
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block py-2 text-base font-medium text-gray-700 hover:text-pink-500 transition-colors cursor-pointer"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <i className="ri-admin-line mr-2"></i>
                    Admin
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleSignOut();
                    setShowMobileMenu(false);
                  }}
                  className="block w-full text-left py-2 text-base font-medium text-red-600 cursor-pointer"
                >
                  <i className="ri-logout-box-line mr-2"></i>
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setShowAuthModal(true);
                  setShowMobileMenu(false);
                }}
                className="w-full mt-3 px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Sign In
              </button>
            )}
          </div>
        )}

        {/* Search Modal */}
        {showSearch && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20 px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1 relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search characters or series..."
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-base"
                      autoFocus
                    />
                  </div>
                  <button
                    onClick={handleSearchClose}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>

                {searching && (
                  <div className="text-center py-8">
                    <i className="ri-loader-4-line text-3xl text-pink-500 animate-spin"></i>
                  </div>
                )}

                {!searching && searchQuery.trim().length >= 2 && (
                  <div className="space-y-6 max-h-96 overflow-y-auto">
                    {/* Characters Results */}
                    {searchResults.characters.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Characters</h3>
                        <div className="space-y-2">
                          {searchResults.characters.map((character) => (
                            <Link
                              key={character.id}
                              to={`/character/${character.id}`}
                              onClick={handleResultClick}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <img
                                src={character.image_url}
                                alt={character.name}
                                className="w-12 h-12 rounded-lg object-cover object-top"
                              />
                              <div>
                                <div className="font-medium text-gray-900">{character.name}</div>
                                <div className="text-sm text-gray-500">{character.anime_name}</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Series Results */}
                    {searchResults.series.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Series</h3>
                        <div className="space-y-2">
                          {searchResults.series.map((series) => (
                            <Link
                              key={series.name}
                              to={`/series?name=${encodeURIComponent(series.name)}`}
                              onClick={handleResultClick}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <img
                                src={series.cover_image}
                                alt={series.name}
                                className="w-12 h-12 rounded-lg object-cover object-top"
                              />
                              <div>
                                <div className="font-medium text-gray-900">{series.name}</div>
                                <div className="text-sm text-gray-500">{series.character_count} characters</div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No Results */}
                    {searchResults.characters.length === 0 && searchResults.series.length === 0 && (
                      <div className="text-center py-8">
                        <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
                        <p className="text-gray-500">No results found for "{searchQuery}"</p>
                      </div>
                    )}
                  </div>
                )}

                {searchQuery.trim().length < 2 && !searching && (
                  <div className="text-center py-8">
                    <i className="ri-search-line text-4xl text-gray-300 mb-3"></i>
                    <p className="text-gray-500">Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showAuthModal && (
          <AuthModal onClose={() => setShowAuthModal(false)} />
        )}
      </div>
    </nav>
  );
}

// AuthModal Component
function AuthModal({ onClose }: { onClose: () => void }) {
  const { signIn, signUp } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isForgotPassword) {
        // Handle password reset
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        setResetEmailSent(true);
      } else if (isSignUp) {
        await signUp(email, password, username);
        setIsSignedUp(true);
      } else {
        await signIn(email, password);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Reset form when switching modes
  const switchMode = (mode: 'signin' | 'signup' | 'forgot') => {
    setError('');
    setIsSignUp(mode === 'signup');
    setIsForgotPassword(mode === 'forgot');
    setResetEmailSent(false);
    setIsSignedUp(false);
    if (mode !== 'signup') {
      setUsername('');
    }
  };

  // If user just signed up, show verification message
  if (isSignedUp) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-mail-check-line text-3xl text-green-600"></i>
          </div>

          <h2 className="text-2xl font-bold mb-4 text-gray-900">
            Check Your Email
          </h2>

          <p className="text-gray-600 mb-6">
            We've sent a verification link to <strong>{email}</strong>. Please check your email and click the link to verify your account.
          </p>

          <button
            onClick={onClose}
            className="w-full py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap"
          >
            Got it!
          </button>
        </div>
      </div>
    );
  }

  // If password reset email sent
  if (resetEmailSent) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative text-center">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
            <i className="ri-mail-send-line text-3xl text-blue-600"></i>
          </div>

          <h2 className="text-2xl font-bold mb-4 text-gray-900">
            Reset Link Sent
          </h2>

          <p className="text-gray-600 mb-6">
            We've sent a password reset link to <strong>{email}</strong>. Check your email and follow the instructions to reset your password.
          </p>

          <button
            onClick={() => switchMode('signin')}
            className="w-full py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap mb-4"
          >
            Back to Sign In
          </button>

          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-pink-500 cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
          {isForgotPassword ? 'Reset Password' : isSignUp ? 'Sign Up' : 'Sign In'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                placeholder="Choose a username"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              placeholder="your@email.com"
            />
          </div>

          {!isForgotPassword && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line`}></i>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? 'Loading...' : 
             isForgotPassword ? 'Send Reset Link' :
             isSignUp ? 'Sign Up' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          {!isForgotPassword ? (
            <>
              {/* Forgot Password Link - only show for sign in */}
              {!isSignUp && (
                <button
                  onClick={() => switchMode('forgot')}
                  className="block w-full text-sm text-gray-600 hover:text-pink-500 cursor-pointer whitespace-nowrap"
                >
                  Forgot your password?
                </button>
              )}

              {/* Sign Up/Sign In Toggle - Made Bigger */}
              <button
                onClick={() => switchMode(isSignUp ? 'signin' : 'signup')}
                className="block w-full text-base font-medium text-gray-700 hover:text-pink-500 cursor-pointer whitespace-nowrap py-2"
              >
                {isSignUp
                  ? 'Already have an account? Sign In'
                  : "Don't have an account? Sign Up"}
              </button>
            </>
          ) : (
            <button
              onClick={() => switchMode('signin')}
              className="text-sm text-gray-600 hover:text-pink-500 cursor-pointer whitespace-nowrap"
            >
              Back to Sign In
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
